import React, { useState, useEffect } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import InactivityLock from './InactivityLock';
import MessagePopup from './MessagePopup';
import PoleFilterBanner from './PoleFilterBanner';
import MessagerieFloating from '../Messagerie/MessagerieFloating';
import { ChatDrawersStack } from '../Messagerie/ChatDrawersStack';
import { Menu, X, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

interface MainLayoutProps {
  children: React.ReactNode;
}

// Clé localStorage pour l'état plié/déplié du menu latéral desktop
const SIDEBAR_COLLAPSED_KEY = 'sidebar_desktop_collapsed';

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // ouverture overlay mobile
  // Pli / dépli desktop — persisté pour restaurer la préférence utilisateur.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar :
          - Mobile : hors écran par défaut, glisse en overlay via `sidebarOpen`
          - Desktop : occupe sa largeur naturelle, ou se replie à 0 (collapsed).
            La transition sur `width` + `opacity` donne l'animation fluide. */}
      <div
        className={`fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 lg:relative lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:transition-[width,opacity] lg:duration-300 lg:overflow-hidden
          ${collapsed ? 'lg:w-0 lg:opacity-0 lg:pointer-events-none' : 'lg:w-auto lg:opacity-100'}`}
      >
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header avec bouton burger mobile + bouton pli desktop */}
        <div className="flex items-center">
          {/* Bouton mobile : ouvre/ferme l'overlay */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          {/* Bouton desktop : plie / déplie la sidebar (animation fluide) */}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="hidden lg:inline-flex p-2 ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            aria-label={collapsed ? 'Afficher le menu latéral' : 'Masquer le menu latéral'}
            title={collapsed ? 'Afficher le menu latéral (Ctrl+B)' : 'Masquer le menu latéral (Ctrl+B)'}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <div className="flex-1">
            <Header />
          </div>
        </div>
        <PoleFilterBanner />
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 lg:p-6">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>
      <InactivityLock />
      <MessagePopup />
      {/* Widget messagerie annuaire : contacts en ligne + chat direct */}
      <MessagerieFloating />
      {/* Stack de drawers multi-conv (style FB Messenger) + raccourcis clavier */}
      <ChatDrawersStack />

      {/* Raccourci clavier Ctrl+B pour toggle rapide */}
      <SidebarShortcut onToggle={() => setCollapsed(c => !c)} />
    </div>
  );
};

// Hook clavier isolé : évite un useEffect direct dans MainLayout pour rester lisible.
const SidebarShortcut: React.FC<{ onToggle: () => void }> = ({ onToggle }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        onToggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onToggle]);
  return null;
};
